import React, { useState } from "react";
import { UserRound, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/AuthContext";
import { formatError } from "../lib/api";

/**
 * Personal Storing toggle — per-user preference.
 *
 * When ON:
 *   • New containers you add are flagged `is_personal=true`
 *   • Only YOU get expiry alerts for those containers (email + push)
 *   • Your name shows a lock next to it on the container card
 *   • Other household members get a warning modal before they can edit
 *
 * This is separate from the Shared/Private radio above — a personal
 * container can still be shared (visible to the whole household) OR
 * private (visible only to you). "Personal" governs who's *notified*,
 * not who can *see*.
 */
export const PersonalStoringCard = () => {
  const { user, updateMe } = useAuth();
  const enabled = Boolean(user?.personal_storing_enabled);
  const [busy, setBusy] = useState(false);

  const toggle = async () => {
    if (busy) return;
    setBusy(true);
    try {
      await updateMe({ personal_storing_enabled: !enabled });
      toast.success(
        !enabled
          ? "Personal storing on — new items are yours alone"
          : "Personal storing off — new items follow household defaults"
      );
    } catch (err) {
      toast.error(formatError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="mt-4 tup-card p-5"
      data-testid="personal-storing-card"
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${
            enabled ? "brand-gradient-bg" : "bg-[#DCEDF0]"
          }`}
        >
          <UserRound className={`w-5 h-5 ${enabled ? "text-white" : "text-[#1F5566]"}`} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-xs uppercase tracking-[0.18em] font-bold text-[#8A93A6]">
            Just for me
          </div>
          <div className="font-heading text-lg font-semibold text-[#2D3142]">
            Personal storing
          </div>
          <p className="text-xs text-[#4F5D75] mt-1.5 leading-relaxed">
            Only you get expiry alerts for items you add — even in a shared
            household. Others see a lock next to your name and get a warning
            before they can edit. Toggle it on or off any time.
          </p>
        </div>
        <button
          type="button"
          onClick={toggle}
          disabled={busy}
          role="switch"
          aria-checked={enabled}
          data-testid="personal-storing-toggle"
          className={`relative shrink-0 w-11 h-6 rounded-full transition-colors ${
            enabled ? "bg-[#277A82]" : "bg-[#C9C4B5]"
          } ${busy ? "opacity-60" : ""}`}
        >
          <span
            className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform ${
              enabled ? "translate-x-5" : "translate-x-0"
            } flex items-center justify-center`}
          >
            {busy && <Loader2 className="w-3 h-3 animate-spin text-[#4F5D75]" />}
          </span>
        </button>
      </div>
    </div>
  );
};
